import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  getPreferences,
  changePassword,
  changeEmail,
  openBillingPortal,
} from '../api'
import { getClientId, setSession } from '../session'
import Button from '../components/Button'
import Input from '../components/Input'
import Spinner from '../components/Spinner'
import Badge from '../components/Badge'
import { ErrorAlert, SuccessAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'
import { billingActions } from '../plans'

function Card({ title, description, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="font-semibold text-gray-900">{title}</h2>
      {description && <p className="text-sm text-gray-500 mt-1 mb-4">{description}</p>}
      {children}
    </div>
  )
}

export default function Account() {
  useDocumentTitle('Account')
  const navigate = useNavigate()
  const clientId = getClientId()

  const [prefs, setPrefs] = useState(null)
  const [loading, setLoading] = useState(true)

  // Change email
  const [newEmail, setNewEmail] = useState('')
  const [emailPw, setEmailPw] = useState('')
  const [emailMsg, setEmailMsg] = useState('')
  const [emailErr, setEmailErr] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)

  // Change password
  const [curPw, setCurPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [pwMsg, setPwMsg] = useState('')
  const [pwErr, setPwErr] = useState('')
  const [pwBusy, setPwBusy] = useState(false)

  // Billing
  const [billingErr, setBillingErr] = useState('')
  const [billingBusy, setBillingBusy] = useState(false)

  useEffect(() => {
    if (!clientId) { navigate('/signin'); return }
    getPreferences(clientId)
      .then(setPrefs)
      .catch(() => { if (!getClientId()) navigate('/signin') })
      .finally(() => setLoading(false))
  }, [clientId])

  async function submitEmail(e) {
    e.preventDefault()
    setEmailMsg(''); setEmailErr(''); setEmailBusy(true)
    try {
      const { message } = await changeEmail(newEmail, emailPw)
      setEmailMsg(message)
      setNewEmail(''); setEmailPw('')
      // Reflect the new email locally + re-fetch prefs.
      setPrefs(p => p ? { ...p, email: newEmail, email_status: 'unconfirmed' } : p)
    } catch (err) {
      setEmailErr(err.message || 'Could not update email.')
    } finally {
      setEmailBusy(false)
    }
  }

  async function submitPassword(e) {
    e.preventDefault()
    setPwMsg(''); setPwErr(''); setPwBusy(true)
    try {
      const { message } = await changePassword(curPw, newPw)
      setPwMsg(message)
      setCurPw(''); setNewPw('')
    } catch (err) {
      setPwErr(err.message || 'Could not change password.')
    } finally {
      setPwBusy(false)
    }
  }

  async function manageBilling() {
    setBillingErr(''); setBillingBusy(true)
    try {
      const { portal_url } = await openBillingPortal()
      window.location.assign(portal_url)
    } catch (err) {
      setBillingErr(err.message || 'Could not open billing portal.')
      setBillingBusy(false)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]"><Spinner /></div>
  )

  const unconfirmed = prefs?.email_status && prefs.email_status !== 'ok'
  // Beta accounts have no Stripe subscription — show a free-plan card and hide
  // the billing-portal button (which needs a stripe_customer_id).
  const isBeta = prefs?.plan_status === 'beta'
  // A trial has no Stripe customer yet, so the portal would refuse it ("No
  // billing account yet"): it is offered the plans instead. An ended plan gets
  // both. The rule lives in plans.js.
  const billing = billingActions(prefs)
  const plansOnly = billing.choosePlan && !billing.manageBilling

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
      <div>
        <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700">
          ← Back to Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">Account Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your login and subscription.</p>
      </div>

      {/* Email */}
      <Card
        title="Email address"
        description="Your account email and where digests are sent."
      >
        <div className="flex items-center gap-2 mb-4 text-sm">
          <span className="font-medium text-gray-900">{prefs?.email}</span>
          {unconfirmed
            ? <Badge variant="soon">Unconfirmed</Badge>
            : <Badge variant="open">Confirmed</Badge>}
        </div>
        <form onSubmit={submitEmail} className="space-y-3">
          <SuccessAlert message={emailMsg} />
          <ErrorAlert message={emailErr} />
          <Input
            label="New email"
            type="email"
            required
            autoComplete="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="new@nonprofit.org"
          />
          <Input
            label="Current password"
            type="password"
            required
            autoComplete="current-password"
            value={emailPw}
            onChange={e => setEmailPw(e.target.value)}
            hint="Changing your email pauses digests until you confirm the new address."
          />
          <Button type="submit" loading={emailBusy} size="sm">Update email</Button>
        </form>
      </Card>

      {/* Password */}
      <Card title="Password" description="Choose a strong password you don't use elsewhere.">
        <form onSubmit={submitPassword} className="space-y-3">
          <SuccessAlert message={pwMsg} />
          <ErrorAlert message={pwErr} />
          <Input
            label="Current password"
            type="password"
            required
            autoComplete="current-password"
            value={curPw}
            onChange={e => setCurPw(e.target.value)}
          />
          <Input
            label="New password"
            type="password"
            required
            autoComplete="new-password"
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            hint="At least 8 characters."
          />
          <Button type="submit" loading={pwBusy} size="sm">Change password</Button>
        </form>
      </Card>

      {/* Billing */}
      {isBeta ? (
        <Card
          title="Billing & subscription"
          description="Your beta plan. No billing while you're helping us test."
        >
          <p className="text-sm text-gray-600">
            Plan: <span className="font-medium capitalize">{prefs?.plan_tier || 'Pro'} (beta, free)</span>
          </p>
          <p className="text-xs text-gray-500 mt-3">
            You're on a free beta account: no card, no charges. We'll reach out
            before the beta ends about continuing.
          </p>
        </Card>
      ) : (
        <Card
          title="Billing & subscription"
          description={plansOnly
            ? 'Choose a plan to keep your digests coming. Cancel anytime.'
            : 'Update your card, switch plans, cancel, or download invoices.'}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              Plan: <span className="font-medium capitalize">{prefs?.plan_tier || 'Not set'}</span>
              {' · '}
              <span className="capitalize">{prefs?.plan_status || 'Not set'}</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {billing.choosePlan && (
                <Button size="sm" onClick={() => navigate('/plans')}>
                  Choose a plan →
                </Button>
              )}
              {billing.manageBilling && (
                <Button variant="secondary" size="sm" loading={billingBusy} onClick={manageBilling}>
                  Manage billing →
                </Button>
              )}
            </div>
          </div>
          <ErrorAlert message={billingErr} />
          <p className="text-xs text-gray-500 mt-3">
            {plansOnly
              ? "Checkout is on Stripe's secure page. We never see your card details."
              : 'Opens the secure Stripe billing portal. We never see your card details.'}
          </p>
        </Card>
      )}
    </div>
  )
}
